import type { ClinicBrief, SessionUser } from '@docsystem/shared';

/**
 * Access token lives in memory only.
 *
 * Deliberately not localStorage: an XSS bug should not yield a durable credential.
 * The refresh token is an httpOnly cookie the page cannot read, so a full reload
 * silently re-establishes the session through /auth/refresh.
 *
 * The display token is the exception — a kiosk has no user to sign it back in, so
 * it persists per device under a separate key with a narrow, revocable scope.
 */

const DISPLAY_TOKEN_KEY = 'dts.displayToken';

interface AuthState {
  accessToken: string | null;
  user: SessionUser | null;
  clinic: ClinicBrief | null;
}

type Listener = (state: AuthState) => void;

let state: AuthState = { accessToken: null, user: null, clinic: null };
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener(state);
}

export const authStore = {
  getState(): AuthState {
    return state;
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  setSession(accessToken: string, user: SessionUser, clinic: ClinicBrief): void {
    state = { accessToken, user, clinic };
    emit();
  },

  setAccessToken(accessToken: string): void {
    state = { ...state, accessToken };
    emit();
  },

  clear(): void {
    state = { accessToken: null, user: null, clinic: null };
    emit();
  },

  get accessToken(): string | null {
    return state.accessToken;
  },
};

// ── Display credential (kiosk only) ───────────────────────────────────────

export function getDisplayToken(): string | null {
  try {
    return window.localStorage.getItem(DISPLAY_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setDisplayToken(token: string): void {
  try {
    window.localStorage.setItem(DISPLAY_TOKEN_KEY, token);
  } catch {
    /* private browsing — the token stays in the URL for this session */
  }
}

export function clearDisplayToken(): void {
  try {
    window.localStorage.removeItem(DISPLAY_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * The stored screen credential, if this device has signed in.
 *
 * A token used to be accepted from `?dt=…` in the address bar. That is gone: a link
 * that carries a credential gets forwarded, bookmarked, screenshotted and pasted into
 * chat, and every copy is a working key to the board. Screens now sign in at /display
 * with a room username and password, which can be rotated without reissuing links.
 */
export function readDisplayToken(): string | null {
  return getDisplayToken();
}
